import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Site, { initialSite } from "../types/site";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface SiteState {
  site: Site;
  siteFilter: SearchParams;
  siteSearchResults?: SearchResults;
  siteAttachmentsFilter: SearchParams;
  status: "idle" | "loading" | "failed";
}

const initialState: SiteState = {
  site: initialSite,
  siteFilter: defaultParams,
  siteSearchResults: undefined,
  siteAttachmentsFilter: defaultParams,
  status: "idle",
};

export const getSiteId = createAsyncThunk<Site, number>(
  "site/get/id",
  async (id: number) => {
    const response = await http.get<Site>(`/site/${id}`);
    return response.data;
  }
);

export const editSite = createAsyncThunk<Site, Site>(
  "site/edit/",
  async (site: Site) => {
    const response = await http.put<Site>(`/site/${site.id}`, site, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const deleteSite = createAsyncThunk<Site, number>(
  "site/delete",
  async (id: number) => {
    const response = await http.delete<Site>(`/site/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postSite = createAsyncThunk<Site, Site>(
  "site/post",
  async (site: Site) => {
    const response = await http.post<Site>(`/site`, site, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchSite = createAsyncThunk<SearchResults, SearchParams>(
  "site/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`site/search?${qs}`);
    return response.data;
  }
);

const siteSlice = createSlice({
  name: "site",
  initialState,
  reducers: {
    resetSite(state) {
      state.site = { ...initialState.site };
    },
    setSiteFilter(state, action) {
      state.siteFilter = action.payload;
    },
    setSiteAttachmentsFilter(state, action) {
      state.siteFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSiteId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSiteId.fulfilled, (state, action) => {
        state.status = "idle";
        state.site = action.payload;
      })
      .addCase(getSiteId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editSite.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editSite.fulfilled, (state, action) => {
        state.status = "idle";
        state.site = action.payload;
      })
      .addCase(editSite.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteSite.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteSite.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteSite.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postSite.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postSite.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postSite.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchSite.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchSite.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteSearchResults = action.payload;

        //TODO: Add back in
        if (window.location.href.includes("job")) {
          if (action.payload.results.length <= 0) {
            let newSite = [{ id: -1, name: "New Site" }];
            state.siteSearchResults.results = newSite;
            state.siteSearchResults.totalResults =
              state.siteSearchResults.totalResults + 1;
          } else if (action.payload.results.length < 10) {
            let newSite = { id: -1, name: "New Site" };
            state.siteSearchResults.results.unshift(newSite);
            state.siteSearchResults.totalResults =
              state.siteSearchResults.totalResults + 1;
          }
        }
      })
      .addCase(searchSite.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default siteSlice.reducer;
export const { resetSite, setSiteFilter, setSiteAttachmentsFilter } =
  siteSlice.actions;
